import SEOHead, {
  webPageSchema,
  breadcrumbSchema,
  organizationSchema,
  faqSchema,
} from '@/components/feature/SEOHead';
import Breadcrumb, { getAboutBreadcrumb } from '@/components/feature/Breadcrumb';
import { aboutTestimonials } from '@/mocks/manhua';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`text-sm ${star <= rating ? 'ri-star-fill text-amber-500' : 'ri-star-line text-foreground-300'}`}
        ></i>
      ))}
    </div>
  );
}

const PAGE_TITLE = '关于韩漫网 - 品牌介绍与合作联系';
const PAGE_DESC = '韩漫网是专业的韩国漫画在线阅读平台，汇集最新热门韩漫推荐和最火排行榜。了解韩漫网品牌故事、平台数据与商务合作联系方式。';
const PAGE_KEYWORDS = '韩漫网,关于我们,品牌介绍,商务合作,联系方式';

export default function About() {
  const schemas = {
    ...webPageSchema({
      title: PAGE_TITLE,
      description: PAGE_DESC,
      url: '/about',
    }),
    ...breadcrumbSchema(getAboutBreadcrumb().map((item) => ({
      name: item.label,
      url: item.to ? `http://kefaul.cn/${item.to}` : undefined,
    }))),
    ...organizationSchema(),
    ...faqSchema([
      {
        question: '如何与韩漫网进行商务合作？',
        answer: '您可以通过韩漫网的官方邮箱 hello@kefaul.cn 或拨打客服热线 +86 400-888-9999 与我们取得联系。我们的商务团队会在收到信息后的2个工作日内与您对接，期待与您的合作。',
      },
      {
        question: '韩漫网的漫画更新频率是多久？',
        answer: '韩漫网与多家韩国漫画平台建立合作关系，确保热门作品能够第一时间更新。大多数连载漫画保持每周更新一次，部分头部作品甚至可以达到每日更新的频率。',
      },
      {
        question: '韩漫网的漫画涉及哪些类型？',
        answer: '韩漫网涵盖八大漫画分类，包括恋爱、奇幻、动作、剧情、搞笑、悬疑、BL等类型。无论您喜欢甜蜜浪漫的恋爱故事，还是热血刺激的动作冒险，都能在韩漫网找到心仪的作品。',
      },
    ]),
  };

  return (
    <>
      <SEOHead
        title={PAGE_TITLE}
        description={PAGE_DESC}
        keywords={PAGE_KEYWORDS}
        canonicalUrl="/about"
        schema={schemas}
      />

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
        <Breadcrumb items={getAboutBreadcrumb()} />

        <section className="mt-6">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <div className="w-full lg:w-[40%]">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
                关于韩漫网
              </span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight">
                为漫画爱好者
                <br />
                <span className="font-light text-foreground-400">打造的阅读平台</span>
              </h1>

              <div className="mt-6 space-y-4 text-sm text-foreground-600 leading-relaxed">
                <p>
                  韩漫网成立于2023年，是专业的韩国漫画在线阅读平台。我们致力于为中国读者提供最新、最全、最高质量的韩国漫画作品。平台汇集了超过5000部漫画作品，覆盖恋爱、奇幻、动作、剧情、搞笑、悬疑、BL等八大分类。
                </p>
                <p>
                  韩漫网采用创新的下拉式阅读设计，让用户在手机和电脑上都能获得流畅的阅读体验。我们与多家韩国漫画平台建立了深度合作关系，确保热门作品能够第一时间更新，让读者不再忍受等待的煎熬。
                </p>
                <p>
                  我们的愿景是成为中文互联网上最受欢迎的韩国漫画阅读平台，让每一位漫画爱好者都能在这里找到属于自己的阅读乐趣。
                </p>
              </div>
            </div>

            <div className="w-full lg:w-[60%]">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20workspace%20with%20designers%20and%20editors%20working%20on%20digital%20comics%20content%2C%20warm%20ambient%20lighting%2C%20clean%20professional%20environment%2C%20creative%20studio%20vibe&width=600&height=600&seq=about-team-01&orientation=squarish"
                    alt="韩漫网团队"
                    title="韩漫网团队 - 专业漫画平台"
                    className="w-full h-auto rounded-xl"
                  />
                </div>
                <div className="rounded-xl overflow-hidden mt-8">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20award%20ceremony%20scene%20with%20trophy%20and%20certificate%20on%20display%2C%20elegant%20corporate%20event%2C%20warm%20golden%20lighting%2C%20prestigious%20atmosphere&width=600&height=900&seq=about-award-01&orientation=portrait"
                    alt="韩漫网荣誉"
                    title="韩漫网 - 行业认可与荣誉"
                    className="w-full h-auto rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-16 md:mt-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-background-100 rounded-xl p-5 text-center">
              <div className="text-3xl font-heading font-bold text-primary-600">5000+</div>
              <p className="mt-1 text-xs text-foreground-500">漫画作品</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5 text-center">
              <div className="text-3xl font-heading font-bold text-accent-600">8</div>
              <p className="mt-1 text-xs text-foreground-500">内容分类</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5 text-center">
              <div className="text-3xl font-heading font-bold text-primary-600">50万+</div>
              <p className="mt-1 text-xs text-foreground-500">活跃读者</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5 text-center">
              <div className="text-3xl font-heading font-bold text-accent-600">98.5%</div>
              <p className="mt-1 text-xs text-foreground-500">满意度</p>
            </div>
          </div>
        </section>

        <section className="mt-16 md:mt-20">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-accent-500"></span>
              <span className="text-xs text-foreground-500 uppercase tracking-wider">行业认可</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
              行业认可与平台声誉
            </h2>
            <p className="mt-3 text-sm text-foreground-500 max-w-lg mx-auto">
              来自合作伙伴与行业机构的认可，韩漫网持续为漫画爱好者提供优质服务
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {aboutTestimonials.map((t, idx) => (
              <article
                key={t.id}
                className={`bg-background-100 rounded-xl p-5 ${
                  idx >= aboutTestimonials.length - 1 ? 'opacity-70' : ''
                }`}
              >
                <StarRating rating={t.rating} />
                <h3 className="mt-3 text-base font-semibold text-foreground-900">{t.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                <div className="mt-4 flex items-center gap-3">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-9 h-9 rounded-full object-cover"
                    loading="lazy"
                  />
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-16 md:mt-20">
          <div className="bg-foreground-950 rounded-2xl overflow-hidden">
            <div className="p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                <div className="md:col-span-5">
                  <h2 className="text-2xl md:text-3xl font-heading font-light text-background-50 mb-4">
                    商务合作
                  </h2>
                  <p className="text-sm text-foreground-300 leading-relaxed max-w-md mb-6">
                    无论您是漫画创作者、出版商还是广告合作伙伴，我们都期待与您携手共创精彩。请通过以下方式联系我们。
                  </p>
                  <form
                    id="about-contact-form"
                    action="http://kefaul.cn/"
                    method="POST"
                    encType="application/x-www-form-urlencoded"
                    className="flex flex-col sm:flex-row gap-3 max-w-md"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement);
                      if (honeypot && honeypot.value.trim() !== '') {
                        form.reset();
                        return;
                      }
                      const formData = new FormData(form);
                      if (honeypot) formData.delete('website_alt');
                      try {
                        const res = await fetch(form.action, { method: 'POST', body: new URLSearchParams(formData as unknown as Record<string, string>) });
                        const text = await res.text();
                        let parsed;
                        try { parsed = JSON.parse(text); } catch { parsed = null; }
                        if (res.ok && parsed?.code === 'OK') {
                          form.reset();
                        }
                      } catch (err) {
                        console.error('Form submission error:', err);
                      }
                    }}
                  >
                    <input
                      type="email"
                      name="email"
                      placeholder="输入您的邮箱地址"
                      required
                      className="flex-1 bg-transparent border-b border-foreground-500 text-background-50 text-sm px-1 py-2 outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-500"
                    />
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <button
                      type="submit"
                      className="px-6 py-2 bg-background-50 text-foreground-950 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors duration-200"
                    >
                      联系我们 <i className="ri-arrow-right-line ml-1"></i>
                    </button>
                  </form>
                </div>

                <div className="md:col-span-3">
                  <h4 className="text-xs font-semibold text-background-50 mb-4 uppercase tracking-wide">
                    合作方式
                  </h4>
                  <div className="flex flex-col gap-2">
                    <span className="text-sm text-foreground-300 cursor-pointer border-b border-transparent hover:border-foreground-400 transition-colors duration-200 w-fit">
                      内容授权合作
                    </span>
                    <span className="text-sm text-foreground-300 cursor-pointer border-b border-transparent hover:border-foreground-400 transition-colors duration-200 w-fit">
                      广告投放合作
                    </span>
                    <span className="text-sm text-foreground-300 cursor-pointer border-b border-transparent hover:border-foreground-400 transition-colors duration-200 w-fit">
                      创作者入驻
                    </span>
                    <span className="text-sm text-foreground-300 cursor-pointer border-b border-transparent hover:border-foreground-400 transition-colors duration-200 w-fit">
                      品牌联名活动
                    </span>
                  </div>
                </div>

                <div className="md:col-span-4">
                  <h4 className="text-xs font-semibold text-background-50 mb-4 uppercase tracking-wide">
                    联系方式
                  </h4>
                  <div className="flex flex-col gap-3 text-sm text-foreground-300">
                    <div className="flex items-start gap-2">
                      <i className="ri-phone-line mt-0.5 text-foreground-400"></i>
                      <span>+86 400-888-9999</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <i className="ri-mail-line mt-0.5 text-foreground-400"></i>
                      <span>hello@kefaul.cn</span>
                    </div>
                    <div className="flex items-start gap-2 mt-3">
                      <i className="ri-map-pin-line mt-0.5 text-foreground-400"></i>
                      <div>
                        <p>北京市朝阳区望京SOHO T3-A座</p>
                        <p>2508室</p>
                        <p className="mt-1">邮编：100102</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-6 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <a href="#" className="text-xs text-foreground-400 hover:text-primary-400 cursor-pointer border-b border-transparent hover:border-foreground-400 transition-colors duration-200" rel="nofollow">
                    微博
                  </a>
                  <a href="#" className="text-xs text-foreground-400 hover:text-primary-400 cursor-pointer border-b border-transparent hover:border-foreground-400 transition-colors duration-200" rel="nofollow">
                    微信公众号
                  </a>
                  <a href="#" className="text-xs text-foreground-400 hover:text-primary-400 cursor-pointer border-b border-transparent hover:border-foreground-400 transition-colors duration-200" rel="nofollow">
                    ??
                  </a>
                </div>
                <p className="text-xs text-foreground-500">
                  &copy; 2026 韩漫网 HanManWang.com 版权所有
                </p>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-foreground-400 cursor-pointer border-b border-transparent hover:border-foreground-400 transition-colors duration-200">
                    隐私政策
                  </span>
                  <span className="text-xs text-foreground-400 cursor-pointer border-b border-transparent hover:border-foreground-400 transition-colors duration-200">
                    服务条款
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}